import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PhoneNumber, ServerPage } from '../../app/types'
import { useSelector } from 'react-redux'
import { meSelector, sitesSelector } from '../../authentication/redux'
import { api, buildPaginatedUrl } from '../../common/api'
import ErrorAlert from '../../common/components/ErrorAlert'
import { SearchField } from '../../common/components/SearchField'
import { sorterHeader } from '../../common/components/table/SorterHeader'
import { ColumnDefinition, InfiniteScrollTable } from '../../common/components/table/Table'
import { Sort, useInfiniteScroll } from '../../common/components/table/useInfiniteScroll'
import { detailUrl as userDetailUrl } from '../../users/index'
import { detailUrl as flowDetailUrl } from '../../flows/index'
import { detailUrl as ivrDetailUrl } from '../../ivrs/index'
import { detailUrl as cpeDetailUrl } from '../../cpes/index'
import { Link } from '@mui/material'
import { formatPhoneNumber } from '../../common/util/formatters'
import { hasUserPrivilege } from '../../common/util/privilegeUtils'
import { FeaturePrivileges } from '../../common/util/featurePrivileges'
import { useHistory } from 'react-router'
import { siteUrl, supportedTypes } from '../../trunkingsites'

export const NumbersTable = () => {
    const { t } = useTranslation()
    const me = useSelector(meSelector)
    const gbtfSites = useSelector(sitesSelector)
    const history = useHistory()

    const fetchNumbers = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<PhoneNumber>> => {
            const { data } = await api.get(buildPaginatedUrl(`/api/numbers`, pageParam, sortParam, searchTermParam, 10000) + '&activeCustomerId=' + me?.customerOriginId)
            return data
        },
        [me]
    )

    const goToSite = useCallback(async (e, siteProductId: string, url: string) => {
        e.preventDefault()
        history.push(`/switch-site?to-site=${siteProductId}&from-site=${''}&to-url=${url}&from-url=${history.location.pathname}`)
        return false;
    }, [history]);


    const { sort, setSort, setSearchTerm, rows, error, fetchNextPage, hasNextPage, isFetching } = useInfiniteScroll<PhoneNumber>({
        queryKey: `customer-numbers`,
        initialSort: { field: 'number', order: 'desc' },
        fetchFunction: fetchNumbers,
    })

    const isGbtfNumber = useCallback((number: PhoneNumber) => {
        return gbtfSites != null && gbtfSites.find(site => site.productId === number.siteProductId) != null
    }, [gbtfSites])

    const isSupportedTrunkingSite = useCallback((number: PhoneNumber) => {
        if (!me?.trunkingSites) return false
        const trunkingSite = me.trunkingSites.find(site => site.productId === number.siteProductId)
        return trunkingSite != null && supportedTypes.find(it => it === trunkingSite.type)

    }, [me])

    const fmuEnabled = useCallback(() => !!me?.gbtfSites?.find(site => site.fmuEnabled)
        , [me])

    const resolveDetailUrl = useCallback((record: PhoneNumber): string | null => {
        if (record.type === 'user' && hasUserPrivilege(me, FeaturePrivileges.users.read, record.siteProductId))
            return userDetailUrl.replace(':id', record.uuid)
        if (record.type === 'ivr' && hasUserPrivilege(me, FeaturePrivileges.ivrs.read, record.siteProductId))
            return ivrDetailUrl.replace(':id', record.uuid)
        if (record.type === 'hot_desk')
            return hasUserPrivilege(me, FeaturePrivileges.devices.read, record.siteProductId)
                ? cpeDetailUrl.replace(':id', record.uuid)
                : null
        if (record.type !== 'user' && record.type !== 'ivr' && record.type !== 'hot_desk'
            && hasUserPrivilege(me, FeaturePrivileges.callFlows.read, record.siteProductId))
            return flowDetailUrl.replace(':id', record.uuid)
        return null
    }, [me])

    // @ts-ignore
    const columns: ColumnDefinition<PhoneNumber>[] = useMemo(
        () => [
            {
                title: t('users.table.extension'),
                key: 'extension',
                sorter: true,
                render: (text: string, record: PhoneNumber) => {
                    const url = isGbtfNumber(record) ? resolveDetailUrl(record) : null
                    return url ? (
                        <Link href={'#'} onClick={async (e: any) => {
                            await goToSite(e, record.siteProductId, url)
                        }}>{text}</Link>
                    ) : (
                        <>{text}</>
                    )
                },
            },
            {
                title: t('numbers.table.number'),
                key: 'number',
                sorter: true,
                render: (text: string, record: PhoneNumber) => {
                    const url = isGbtfNumber(record) ? resolveDetailUrl(record) : null
                    return url ? (
                        <Link href={'#'} onClick={async (e: any) => {
                            await goToSite(e, record.siteProductId, url)
                        }}>{formatPhoneNumber(text)}</Link>
                    ) : (
                        <>{formatPhoneNumber(text)}</>
                    )
                },
            },
            {
                title: t('site.title'),
                key: 'siteName',
                sorter: true,
                render: (text: string, record: PhoneNumber) =>
                    isGbtfNumber(record) && hasUserPrivilege(me, FeaturePrivileges.site.read, record.siteProductId) ?
                        <Link
                            href={'#'}
                            onClick={async (e: any) => {await goToSite(e, record.siteProductId, '/site')}}>
                            {text}
                        </Link> : isSupportedTrunkingSite(record) && hasUserPrivilege(me, FeaturePrivileges.enterpriseTrunkingSites.read) ?
                            <Link
                                href={siteUrl.replace(':id', record.siteProductId)}>
                                {text}
                            </Link> : <>{text}</>,

            },
            ...(fmuEnabled() ? [{
                title: t('users.table.fmu-msisdn'),
                key: 'fmuMsisdn',
                sorter: true,
                render: (text: string) => <>{formatPhoneNumber(text)}</>,
            }] : []),
            {
                title: t('numbers.table.type'),
                key: 'type',
                sorter: false,
                render: (text: string) => <>{text ? t(`numbers.types.${text}`) : ''}</>,
            },
            {
                title: t('numbers.table.assigned-to'),
                key: 'assignedTo',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
        ],
        [t, fmuEnabled, goToSite, isGbtfNumber, isSupportedTrunkingSite, resolveDetailUrl, me],
    )

    const sorter = useCallback(col => sorterHeader(col, sort, setSort), [sort, setSort])

    const getRowKey = useCallback(cpe => cpe.uuid + '+' + cpe.number, [])

    const onSearch = useCallback(async (searchTerm: string) => setSearchTerm(searchTerm), [setSearchTerm])

    return (
        <>
            <SearchField key={`search-numbers-customer`} onSearch={onSearch} />
            <ErrorAlert key={`search-numbers-customer-error`} showAlert={!!error} />
            <InfiniteScrollTable<PhoneNumber>
                maxHeight={'55vh'}
                hasNextPage={hasNextPage}
                data={rows}
                fetchNextPage={fetchNextPage}
                isFetching={isFetching}
                getRowKey={getRowKey}
                sorter={sorter}
                columns={columns}
                tableId={'numbers-customer'}
            />
        </>
    )
}
